import React, { useState, useRef, useEffect } from 'react';
import { Menu, User, LogOut, Settings, ChevronDown, Bell, HelpCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import './Navigation.css';

import { useLayout } from '../contexts/LayoutContext';

export default function TopBar({ onMenuClick }) {
    const { user, logout } = useAuth();
    const { title, actions } = useLayout();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    return (
        <header className="bg-white border-b border-enterprise-border h-16 flex items-center justify-between px-6 sticky top-0 z-40 font-sans">
            {/* Left: Mobile Menu & Breadcrumbs */}
            <div className="flex items-center min-w-[200px]">
                <button
                    onClick={onMenuClick}
                    className="md:hidden p-2 rounded-enterprise text-enterprise-muted hover:bg-slate-50 mr-2 transition-colors"
                >
                    <Menu className="w-5 h-5" />
                </button>

                <div className="hidden md:flex items-center text-sm font-medium">
                    <Link to="/" className="text-enterprise-muted hover:text-coop-600 transition-colors">Inicio</Link>
                    {title && title.split(' > ').map((segment, index) => (
                        <React.Fragment key={index}>
                            <ChevronRight className="w-4 h-4 mx-2 text-slate-300" />
                            <span className={index === title.split(' > ').length - 1 ? "text-slate-900 font-bold" : "text-enterprise-muted"}>
                                {segment}
                            </span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Right Side: Actions & Profile */}
            <div className="flex items-center space-x-2">
                {/* Ask Button */}
                <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-enterprise border border-enterprise-border text-enterprise-muted hover:bg-slate-50 transition-all text-sm font-medium">
                    <HelpCircle className="w-4 h-4 text-coop-600" />
                    <span>Ayuda</span>
                </button>

                {/* Notifications */}
                <button className="p-2 rounded-enterprise text-enterprise-muted hover:text-coop-600 hover:bg-slate-50 transition-all relative">
                    <Bell className="w-5 h-5" />
                    <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-rose-500 rounded-full border border-white"></span>
                </button>

                <div className="h-4 w-px bg-slate-200 mx-2"></div>

                {/* User Profile */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-enterprise transition-all ${isDropdownOpen ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                    >
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 border border-slate-300">
                            <User className="w-4 h-4" />
                        </div>
                        <div className="hidden lg:block text-left">
                            <p className="text-[11px] font-bold text-slate-900 leading-tight">{user?.username || user?.nombre}</p>
                            <p className="text-[10px] text-enterprise-muted leading-tight font-medium">Administrador</p>
                        </div>
                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-enterprise shadow-premium border border-enterprise-border py-0 z-50 overflow-hidden animate-in fade-in slide-in-from-top-1">
                            {/* Header del Dropdown */}
                            <div className="px-4 py-4 border-b border-slate-100 bg-slate-50/50">
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Identidad</div>
                                        <div className="font-bold text-slate-900 text-sm leading-tight">{user?.username || user?.nombre}</div>
                                    </div>

                                    <div className="space-y-1">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sucursal</div>
                                        <div className="font-semibold text-slate-700 text-xs">{user?.sucursalNombre || 'Sucursal Central'}</div>
                                    </div>

                                    <div className="pt-1">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Permisos</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(user?.roles || []).map((rol, idx) => (
                                                <span key={`${rol}-${idx}`} className="enterprise-badge bg-slate-100 text-slate-600 border border-slate-200">
                                                    {rol}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Acciones */}
                            <div className="p-1">
                                <button
                                    onClick={() => {
                                        logout();
                                        setIsDropdownOpen(false);
                                    }}
                                    className="flex w-full items-center px-4 py-2.5 text-xs text-rose-600 hover:bg-rose-50 font-bold transition-all rounded-enterprise group"
                                >
                                    <LogOut className="w-3.5 h-3.5 mr-3 transition-transform group-hover:-translate-x-1" />
                                    <span>Cerrar Sesión</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
